import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';
import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { useHandleGiftCardResponse } from '@order/features-gift-card';
import { type ApplyGiftCardParams, type ApplyGiftCardResponse } from '@order/features-ordering';
import { getFetchOptions } from '@order/utils';

import {
  BagGiftCardBalanceDocument,
  type BagGiftCardBalanceQuery,
  RedeemGiftCardInBagDocument,
  type RedeemGiftCardInBagMutation,
  type RedeemGiftCardInBagMutationVariables,
} from '../../GraphQL/BagGiftCard.generated';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching gift card balance and applying gift cards in the bag.
 */
export const useGiftCards = (fetchCredits: () => Promise<number>) => {
  const client = useClient();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { handleGiftCardResponse } = useHandleGiftCardResponse();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const fetchGiftCardBalance = useCallback(async () => {
    const queryRewards = client.query<BagGiftCardBalanceQuery>;

    const response = await queryRewards(
      BagGiftCardBalanceDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();

    if (response.data?.giftCardBalance.__typename !== 'GiftCardBalance') {
      addNoticeBanner({
        text: formatMessage(messages.fetchGiftCardsError),
        palette: 'caution',
      });

      return 0;
    }

    return response.data.giftCardBalance.giftCardBalance;
  }, [client.query, formatMessage, addNoticeBanner]);

  // ─── Mutations ────────────────────────────────────────────────────────────

  const applyGiftCard = useMemo(
    () =>
      fromPromise<ApplyGiftCardResponse, ApplyGiftCardParams>(async ({ input }) => {
        const { code, regCode, awsWafToken } = input;

        const mutation = client.mutation<
          RedeemGiftCardInBagMutation,
          RedeemGiftCardInBagMutationVariables
        >;

        const { data, error } = await mutation(
          RedeemGiftCardInBagDocument,
          { code, regCode },
          {
            requestPolicy: 'network-only',
            ...(awsWafToken
              ? {
                  fetchOptions: getFetchOptions({
                    extraHeaders: {
                      'x-aws-waf-token': awsWafToken,
                    },
                  }),
                }
              : {}),
          },
        ).toPromise();

        const response = data?.redeemGiftCard;

        await handleGiftCardResponse({ response, error, source: 'bag' });

        if (response?.__typename === 'GiftCardBalance') {
          const availableCredit = await fetchCredits();
          const { giftCardBalance } = response;

          return { giftCardBalance, availableCredit };
        }

        return null;
      }),
    [client.mutation, fetchCredits, handleGiftCardResponse],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchGiftCardBalance, applyGiftCard };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  fetchGiftCardsError: {
    defaultMessage: 'Something went wrong fetching your gift cards.',
    description: 'Bag | Gift card | Gift card fetch error',
  },
});
